/**
 * CharacterController: walk-style first-person controller.
 *
 * Adapted from supersplat-viewer/src/cameras/walk-controller.ts.
 * Capsule collision with walls/ceiling, spring-damper suspension over terrain,
 * gravity + jump, horizontal movement constrained to the XZ plane (yaw-only).
 * Requires collision data; the demo falls back to FirstPersonController when
 * none is available.
 *
 * `move` input: [strafeDir, jumpFlag, forwardDir] (horizontal already scaled by
 * the input layer for Shift/Ctrl); move[1] is 0/1 (jump).
 */

import { math, Vec3 } from 'playcanvas';

import type { Collision, PushOut } from '../collision';
import { findCylinderSpawn } from '../collision';
import { CameraState } from './types';
import type { CameraFrame, CameraController } from './types';
import { DEFAULT_CONTROLLER_DAMPING, applyFrameRotation, dampAngles, setBasisOffset, setYawBasis } from './camera-utils';
import { SpawnState } from './spawn-state';

const FIXED_DT = 1 / 60;
const MAX_SUBSTEPS = 10;

const out: PushOut = { x: 0, y: 0, z: 0 };

const v = new Vec3();
const d = new Vec3();
const forward = new Vec3();
const right = new Vec3();
const moveStep = [0, 0, 0];
const offset = new Vec3();
const spawnProbe = new Vec3();

class CharacterController implements CameraController {
    collision: Collision | null = null;

    fov = 75;

    /** Total capsule height in meters. */
    capsuleHeight = 1.5;
    /** Capsule radius in meters. */
    capsuleRadius = 0.12;
    /** Camera height from the bottom of the capsule. */
    eyeHeight = 1.3;
    /** Gravity (m/s^2). */
    gravity = 9.8;
    /** Jump velocity (m/s). */
    jumpSpeed = 4;
    /** Movement speed when grounded (m/s). */
    moveGroundSpeed = 2.5;
    /** Movement speed when airborne (air control, m/s). */
    moveAirSpeed = 1;
    /** Per-substep lerp toward target horizontal velocity (ground / air). */
    groundAccel = 0.3;
    airAccel = 0.1;
    rotateDamping = DEFAULT_CONTROLLER_DAMPING;
    /** Target clearance from capsule bottom to ground. */
    hoverHeight = 0.2;
    springStiffness = 800;
    springDamping = 57;
    groundProbeRange = 2.0;
    /** Vertical gap tolerated before grounded movement becomes airborne. */
    groundDetachHeight = 0.35;
    /** Highest nearby step/ledge that can be treated as walkable floor. */
    maxStepHeight = 0.45;
    /** If the player falls below this Y, reset to spawn (safety net). -Infinity disables. */
    fallLimit = -Infinity;

    /** Pitch clamp (degrees) applied to mouse look — overridable by subclasses. */
    protected _minPitch = -90;
    protected _maxPitch = 90;

    protected _position = new Vec3();
    protected _prevPosition = new Vec3();
    protected _angles = new Vec3();
    protected _targetAngles = new Vec3();
    protected _spawn = new SpawnState();
    protected _spawnGrounded = false;
    protected _velocity = new Vec3();
    protected _scratchState = new CameraState();
    protected _pendingMove = [0, 0, 0];
    protected _accumulator = 0;
    protected _grounded = false;
    /** 最近一次地面探测高度（_probeGround 结果；null = 无地面/无碰撞体）。
     *  本地渲染不消费它——网络上行用它做贴地钳制：上坡爬升期弹簧把眼位压在
     *  ground+hover+eye 目标线之下，按目标线发送可让远端重建的脚底不陷入坡面。 */
    private _lastGroundY: number | null = null;
    get lastGroundY(): number | null { return this._lastGroundY; }
    onNetworkReset?: () => void;
    get networkGrounded() { return this._grounded; }
    get networkJumping() { return this._jumping; }
    get networkRunning() { return this._lastInputRunning; }
    protected _jumping = false;
    protected _jumpHeld = false;
    protected _lastInputRunning = false;

    onEnter(state: CameraState): void {
        this.goto(state);
        if (this.collision) {
            this._spawn.clear();
            if (findCylinderSpawn(this.collision,
                state.position.x, state.position.y, state.position.z,
                (this.capsuleHeight + this.hoverHeight) * 0.5, this.capsuleRadius, spawnProbe)) {
                this._position.set(
                    spawnProbe.x,
                    spawnProbe.y + this.hoverHeight + this.eyeHeight,
                    spawnProbe.z
                );
                this._grounded = true;
                this._velocity.y = 0;
                this._lastGroundY = spawnProbe.y;
                this._storeSpawn();
                state.position.copy(this._position);
                state.fov = this.fov;
            }
            this._prevPosition.copy(this._position);
        }
    }

    update(deltaTime: number, inputFrame: CameraFrame, state: CameraState) {
        const { move, rotate, running } = inputFrame.read();
        this._lastInputRunning = running;

        applyFrameRotation(this._targetAngles, rotate, this._minPitch, this._maxPitch);
        dampAngles(this._angles, this._targetAngles, this.rotateDamping, deltaTime);

        this._pendingMove[0] = move[0];
        this._pendingMove[1] = this._pendingMove[1] || move[1];
        this._pendingMove[2] = move[2];

        this._accumulator = Math.min(this._accumulator + deltaTime, MAX_SUBSTEPS * FIXED_DT);
        const numSteps = Math.floor(this._accumulator / FIXED_DT);

        if (numSteps > 0) {
            moveStep[0] = this._pendingMove[0];
            moveStep[1] = this._pendingMove[1];
            moveStep[2] = this._pendingMove[2];
            for (let i = 0; i < numSteps; i++) {
                this._prevPosition.copy(this._position);
                this._step(FIXED_DT, moveStep);
                this._accumulator -= FIXED_DT;
            }
            this._pendingMove[0] = 0;
            this._pendingMove[1] = 0;
            this._pendingMove[2] = 0;
        }

        const alpha = this._accumulator / FIXED_DT;
        state.position.lerp(this._prevPosition, this._position, alpha);
        state.angles.set(this._angles.x, this._angles.y, 0);
        state.fov = this.fov;
    }

    onExit(_state: CameraState): void {
        // nothing
    }

    goto(state: CameraState) {
        this._position.copy(state.position);
        this._prevPosition.copy(this._position);
        this._angles.set(state.angles.x, state.angles.y, 0);
        this._targetAngles.copy(this._angles);
        this._resetMotion();
    }

    resetToSpawn(state: CameraState): boolean {
        if (this.onNetworkReset) { this.onNetworkReset(); return false; }
        if (!this._spawn.has) return false;
        this._spawn.restore(this._position, this._angles);
        this._prevPosition.copy(this._position);
        this._targetAngles.copy(this._angles);
        this._resetMotion();
        this._grounded = this._spawnGrounded;
        state.position.copy(this._position);
        state.angles.copy(this._angles);
        state.fov = this.fov;
        return true;
    }

    private _storeSpawn() {
        this._spawn.store(this._position, this._angles);
        this._spawnGrounded = this._grounded;
    }

    private _resetMotion() {
        this._velocity.set(0, 0, 0);
        this._grounded = false;
        // 位置重置后旧地面高度不再可信，清空防止上行钳制用错基准。
        this._lastGroundY = null;
        this._jumping = false;
        this._jumpHeld = false;
        this._lastInputRunning = false;
        this._pendingMove[0] = 0;
        this._pendingMove[1] = 0;
        this._pendingMove[2] = 0;
        this._accumulator = 0;
    }

    private _step(dt: number, move: number[]) {
        const groundY = this._probeGround(this._position);
        this._lastGroundY = groundY;
        const hasGround = groundY !== null;

        if (this._velocity.y < 0) this._jumping = false;
        if (move[1] && !this._jumping && this._grounded && !this._jumpHeld) {
            this._jumping = true;
            this._velocity.y = this.jumpSpeed;
            this._grounded = false;
        }
        this._jumpHeld = !!move[1];

        if (hasGround && !this._jumping) {
            const targetY = groundY + this.hoverHeight + this.eyeHeight;
            const displacement = this._position.y - targetY;
            if (displacement > this.groundDetachHeight) {
                this._velocity.y -= this.gravity * dt;
                const nextY = this._position.y + this._velocity.y * dt;
                if (nextY <= targetY) { this._position.y = targetY; this._velocity.y = 0; }
                this._grounded = false;
            } else {
                const springForce = -this.springStiffness * displacement - this.springDamping * this._velocity.y;
                this._velocity.y += springForce * dt;
                this._grounded = true;
            }
        } else {
            this._velocity.y -= this.gravity * dt;
            this._grounded = false;
        }

        setYawBasis(this._angles.y, forward, right);
        setBasisOffset(offset, move[0], 0, move[2], forward, right, Vec3.UP);
        // Direct target-velocity model: steer horizontal velocity toward the
        // input direction × speed, with light accel for smoothing. This keeps
        // actual speed == moveGroundSpeed (the old additive+damp model overshot
        // by ~6× and felt uncontrollably fast).
        const speed = this._grounded ? this.moveGroundSpeed : this.moveAirSpeed;
        const accel = this._grounded ? this.groundAccel : this.airAccel;
        this._velocity.x = math.lerp(this._velocity.x, offset.x * speed, accel);
        this._velocity.z = math.lerp(this._velocity.z, offset.z * speed, accel);

        this._position.add(v.copy(this._velocity).mulScalar(dt));

        this._checkCollision(this._position, d);

        // Safety net: if the player slips through a gap, reset to spawn.
        if (this.fallLimit !== -Infinity && this._position.y < this.fallLimit) {
            this.resetToSpawn(this._dummyState());
        }
    }

    private _dummyState(): CameraState {
        // resetToSpawn writes back into a CameraState; we hand it a scratch one
        // and ignore the result (the controller's own _position is the source
        // of truth, restored by resetToSpawn).
        this._scratchState.position.copy(this._position);
        this._scratchState.angles.copy(this._angles);
        this._scratchState.fov = this.fov;
        return this._scratchState;
    }

    private _probeGround(pos: Vec3): number | null {
        if (!this.collision) return null;
        const oy = pos.y - this.eyeHeight;
        const r = this.capsuleRadius;
        const range = this.groundProbeRange;
        const expectedGroundY = oy - this.hoverHeight;
        const maxSideGroundY = expectedGroundY + this.maxStepHeight;
        const centerHit = this.collision.queryRay(pos.x, oy, pos.z, 0, -1, 0, range);
        if (centerHit) return centerHit.y;

        let totalY = 0;
        let hitCount = 0;
        for (let i = 1; i < 5; i++) {
            let ox = pos.x, oz = pos.z;
            if (i === 1) ox -= r;
            else if (i === 2) ox += r;
            else if (i === 3) oz += r;
            else if (i === 4) oz -= r;
            const hit = this.collision.queryRay(ox, oy, oz, 0, -1, 0, range);
            if (hit && hit.y <= maxSideGroundY) { totalY += hit.y; hitCount++; }
        }
        return hitCount > 0 ? totalY / hitCount : null;
    }

    private _checkCollision(pos: Vec3, disp: Vec3) {
        const center = pos.y - this.eyeHeight + this.capsuleHeight * 0.5;
        const half = this.capsuleHeight * 0.5 - this.capsuleRadius;
        if (this.collision!.queryCapsule(pos.x, center, pos.z, half, this.capsuleRadius, out)) {
            disp.set(out.x, out.y, out.z);
            const landedOnStep = disp.y > 0 && disp.y <= this.maxStepHeight;
            if (disp.y > this.maxStepHeight) disp.y = 0;
            pos.add(disp);
            if (disp.y < 0 && this._velocity.y > 0) this._velocity.y = 0;
            if (!this._grounded && landedOnStep && this._velocity.y < 0) {
                this._velocity.y = 0;
                this._grounded = true;
            }
        }
    }
}

export { CharacterController };
